import React from "react";
import { Link } from "react-router-dom";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, DollarSign, ShieldAlert, FileText, Lock, PhoneCall, Clock, Cloud } from "lucide-react";
import PoweredBy from "@/components/PoweredBy";

const TIERS = [
  {
    name: "Simple return",
    price: "Starts at $285",
    priceNote: "+ bank fees",
    testid: "tier-simple",
    subtitle: "W-2 only · standard deduction",
    features: [
      "Federal + one state included",
      "Direct deposit refund setup",
      "Same-day e-file when docs are complete",
      "24-hour turnaround typical",
    ],
    best: false,
  },
  {
    name: "EIC & CTC",
    price: "Starts at $375",
    priceNote: "+ bank fees",
    testid: "tier-family",
    subtitle: "Dependents · Child Tax Credit · Earned Income Credit",
    features: [
      "Everything in Simple",
      "Unlimited dependents",
      "Child & Dependent Care Credit",
      "Earned Income Tax Credit optimization",
      "Free amendment if you get more docs later",
    ],
    best: true,
  },
  {
    name: "Self-employment",
    price: "Starts at $585",
    priceNote: "+ bank fees",
    testid: "tier-se",
    subtitle: "1099 · Schedule C · rental income",
    features: [
      "Everything in EIC & CTC",
      "Schedule C business income + expenses",
      "Home-office deduction",
      "Quarterly estimated tax setup",
      "Rental property (Schedule E)",
    ],
    best: false,
  },
];

export default function Services() {
  const { branding } = useBranding();
  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/book" className="underline text-muted-foreground hover:text-foreground">Book</Link>
            <Link to="/intake" className="underline text-muted-foreground hover:text-foreground">Start intake</Link>
            <Link to="/client-portal" className="underline text-muted-foreground hover:text-foreground">Check status</Link>
            <Button size="sm" asChild><Link to="/intake" data-testid="services-cta-start">Get started</Link></Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto p-6 lg:p-12 pt-16 pb-12">
        <div className="label-caps mb-3">Services</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-none mb-4">
          Human-prepared returns.<br />
          <span className="italic text-primary">Honest starting prices.</span>
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl">
          Every return is prepared and reviewed by a real person. Each return has a starting price — final cost depends on which forms your return needs (all form pricing is set by TaxSlayer). Bank product fees apply if you choose refund-transfer or advance products.
        </p>
      </section>

      {/* Pricing tiers */}
      <section className="max-w-6xl mx-auto p-6 lg:p-12 py-8">
        <div className="grid md:grid-cols-3 gap-5">
          {TIERS.map((t) => (
            <div
              key={t.name}
              data-testid={t.testid}
              className={`border rounded-md p-6 flex flex-col ${t.best ? "border-primary shadow-lg" : "border-border bg-card"}`}
            >
              {t.best && <div className="label-caps text-[9px] mb-2 text-primary">Most popular</div>}
              <h3 className="font-serif text-2xl">{t.name}</h3>
              <p className="text-xs text-muted-foreground mb-4">{t.subtitle}</p>
              <div className="mb-4">
                <div className="font-serif text-3xl leading-none">{t.price}</div>
                {t.priceNote && <div className="text-[11px] text-muted-foreground mt-1">{t.priceNote}</div>}
              </div>
              <ul className="space-y-2 text-sm mb-6 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 size={14} className="text-primary mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <Button asChild variant={t.best ? "default" : "outline"} className="flex-1">
                  <Link to="/intake" data-testid={`${t.testid}-cta`}>Start intake</Link>
                </Button>
                <Button asChild variant="ghost" className="flex-1">
                  <Link to="/book" data-testid={`${t.testid}-book`}>Or book</Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Cash advance program */}
      <section className="border-t border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 lg:p-12 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 text-primary mb-3">
              <DollarSign size={16} />
              <span className="label-caps">Cash advance program</span>
            </div>
            <h2 className="font-serif text-4xl mb-4">Money before your refund lands.</h2>
            <p className="text-muted-foreground mb-4">
              We're partnered with <strong>Refund Advantage (Pathway)</strong> — one of the biggest tax banks in the country. Once your return is accepted by the IRS, you can apply for an advance of up to <strong>$6,000</strong>, deposited the same day in most cases.
            </p>
            <ul className="space-y-2 text-sm mb-6">
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-primary mt-0.5" /> No prepayment fees — advance is netted from your refund</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-primary mt-0.5" /> Direct deposit or prepaid card</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={14} className="text-primary mt-0.5" /> Approval decided by Refund Advantage — not by us</li>
            </ul>
            <Button asChild data-testid="services-cta-advance"><Link to="/intake">Apply on the intake form <ArrowRight size={12} className="ml-2" /></Link></Button>
          </div>
          <div className="border-l-2 border-primary/30 pl-6">
            <div className="flex items-center gap-2 text-destructive mb-3">
              <ShieldAlert size={14} />
              <span className="label-caps text-[10px]">Legal disclosure</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              If you apply for a cash advance, <strong>{branding.brand_name}</strong> is not responsible for the outcome of the decision. It is solely up to <strong>Refund Advantage (Pathway)</strong>, the tax bank {branding.brand_name} is affiliated with. Advance amount and approval depend on your expected refund, IRS acceptance, and lender criteria. Only apply if you understand the terms.
            </p>
          </div>
        </div>
      </section>

      {/* Audit & amendment policy */}
      <section className="max-w-6xl mx-auto p-6 lg:p-12 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-destructive/30 rounded-md bg-destructive/5 p-6" data-testid="services-audit-policy">
            <div className="flex items-center gap-2 text-destructive mb-3">
              <ShieldAlert size={14} />
              <span className="label-caps text-[10px]">IRS letters &amp; audits</span>
            </div>
            <h3 className="font-serif text-2xl mb-3">If you receive a letter and you don't understand it — contact your preparer.</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              If you received a letter for something that you <strong>opted to leave off of your intake form</strong> — but you knew those forms needed to be filed — <strong>{branding.brand_name}</strong> is not responsible to fix your tax return. Correcting or amending in that situation is a separate charge of <strong>$385 per year</strong>.
            </p>
          </div>
          <div className="border border-border rounded-md bg-card p-6" data-testid="services-amendments">
            <div className="flex items-center gap-2 text-primary mb-3">
              <FileText size={14} />
              <span className="label-caps text-[10px]">Amendments · 1040-X</span>
            </div>
            <h3 className="font-serif text-2xl mb-3">We amend prior-year returns too.</h3>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              Corrected W-2? Missed a credit? Whether we filed the original or someone else did, we'll prepare the 1040-X and file it. Flat fee of <strong>$385 per amendment</strong>.
            </p>
            <Button asChild variant="outline" size="sm" data-testid="services-cta-amend-page"><Link to="/amend">Start an amendment <ArrowRight size={12} className="ml-1.5" /></Link></Button>
          </div>
        </div>
      </section>

      {/* How we work */}
      <section className="border-t border-border bg-card" data-testid="services-how-we-work">
        <div className="max-w-6xl mx-auto p-6 lg:p-12 py-16">
          <div className="max-w-2xl mb-10">
            <div className="label-caps mb-2">How we work</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">Fully web-based. Fully transparent.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <PolicyCard
              icon={<Cloud size={16} />}
              title="Web-based firm"
              body={<>We are a <strong>web-based firm</strong>. All documents are to be uploaded on the <strong>DyNamic Tax intake form</strong> — no fax, no email attachments.</>}
            />
            <PolicyCard
              icon={<PhoneCall size={16} />}
              title="Verbal agreement required"
              body={<><strong>NO RETURNS ARE PROCESSED WITHOUT YOUR VERBAL AGREEMENT.</strong> After our conversation, a screenshot of your refund amounts is sent for your records.</>}
            />
            <PolicyCard
              icon={<Lock size={16} />}
              title="Password-protected returns"
              body={<>All returns are sent <strong>password-protected</strong> to the email you provided <em>before</em> the return is submitted to the IRS. Delete that email and re-sending is <strong>$25 per return</strong>.</>}
            />
            <PolicyCard
              icon={<FileText size={16} />}
              title="Save your screenshots"
              body={<>When trying to check WMR (Where's My Refund) or your IRS transcripts, <strong>refer back to the screenshots we sent you</strong> — refund amount, filing status, and dates.</>}
            />
          </div>
        </div>
      </section>

      {/* Business hours */}
      <section className="max-w-6xl mx-auto p-6 lg:p-12 py-16" data-testid="services-hours">
        <div className="border border-border rounded-md bg-card p-8 flex flex-col md:flex-row items-start gap-8">
          <div className="flex items-center gap-2 text-primary shrink-0">
            <Clock size={16} />
            <span className="label-caps">Business hours</span>
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-3xl mb-4">Sunday – Friday · 9 AM – 6 PM</h3>
            <p className="text-muted-foreground mb-3">
              <strong>Saturdays are by appointment only.</strong> That's the day the office catches up on calls and paperwork so nothing falls through the cracks.
            </p>
            <Button asChild size="sm" data-testid="services-cta-hours-book"><Link to="/book">Book a Saturday appointment <ArrowRight size={12} className="ml-1.5" /></Link></Button>
          </div>
        </div>
      </section>

      {/* Accuracy disclaimer */}
      <section className="max-w-6xl mx-auto p-6 lg:p-12 pb-16">
        <div className="border border-destructive/30 rounded-md bg-destructive/5 p-6 text-sm">
          <div className="flex items-center gap-2 text-destructive mb-2">
            <ShieldAlert size={14} />
            <span className="label-caps text-[10px]">Accuracy disclaimer</span>
          </div>
          <p className="text-muted-foreground">
            <strong>{branding.brand_name}</strong> is not responsible for any letters or audits if information is withheld. Only things that will be inputted to your return are the information provided to your preparer. Please upload every document that pertains to you on the intake form.
          </p>
        </div>
      </section>

      <footer className="border-t border-border bg-card">
        <div className="max-w-6xl mx-auto p-6 flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
          <PoweredBy className="!m-0" />
          <div className="flex gap-4">
            <Link to="/services" className="underline hover:text-foreground">Services</Link>
            <Link to="/intake" className="underline hover:text-foreground">Client intake</Link>
            <Link to="/client-portal" className="underline hover:text-foreground">Client portal</Link>
            <Link to="/pricing" className="underline hover:text-foreground">Course plans</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const PolicyCard = ({ icon, title, body }) => (
  <div className="border border-border rounded-md p-5 bg-background h-full">
    <div className="flex items-center gap-2 text-primary mb-3">{icon}<span className="label-caps text-[10px]">Policy</span></div>
    <h4 className="font-serif text-lg mb-2 leading-tight">{title}</h4>
    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);
